import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import copy from 'copy-to-clipboard';
import AccessTypes from '../../../constants/accessTypes';
import {
  appSubmissionCancelBtn,
  appSubmissionConfirmBtn,
} from '../../../styling/theme';

const Modal = withReactContent(Swal);

export const showAlert = (message, subMessage) => {
  Swal.fire(message, subMessage);
};

export const showError = (
  message = 'Something went wrong!',
  showFooter = true,
) => {
  Swal.fire({
    icon: 'error',
    title: 'Oops...',
    text: message,
    ...(showFooter && { footer: '<a href = "/support">Contact Support</a>' }),
  });
};

export const showSuccess = (message = 'Great Job!') => {
  Swal.fire({
    icon: 'success',
    title: 'Success!',
    text: message,
  });
};

export const showIrisSubmissionSuccess = (
  message,
  handleRedirect,
  handleRedirectToListings,
) => {
  Swal.fire({
    icon: 'success',
    title: 'Success!',
    text: message,
    showCancelButton: true,
    confirmButtonColor: appSubmissionConfirmBtn,
    cancelButtonColor: appSubmissionCancelBtn,
    confirmButtonText: 'Check now',
    cancelButtonText: 'Close',
  }).then((result) => {
    if (result.isConfirmed) {
      handleRedirect({
        activeTab: 'Activity',
        path: 'services/?tab=Activity',
      });
      handleRedirectToListings();
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      handleRedirectToListings();
    }
  });
};

export const showWarning = (title = 'Warning', message = null) => {
  Swal.fire({
    icon: 'warning',
    title,
    text: message,
  });
};

export const showConfirm = async (
  message = 'Are you sure you want to continue?',
  subMessage = '',
  confirmText = 'Continue',
  declineText = 'Cancel',
) => {
  try {
    const result = await Modal.fire({
      title: message,
      html: subMessage,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: confirmText,
      cancelButtonText: declineText,
    });
    if (result.value) {
      return true;
    }
    return false;
  } catch (err) {
    Swal.fire('Something went wrong...', 'Please contact support', 'error');
    return false;
  }
};

export const showModal = async (
  title = 'Options',
  confirmButtonText = 'Choose',
  inputOptions,
) => {
  let inputIsInvalid = true;
  const result = await Modal.fire({
    title,
    confirmButtonText,
    input: 'select',
    inputOptions,
  });
  while (inputIsInvalid) {
    if (result !== undefined) {
      inputIsInvalid = false;
    }
  }
  return result;
};

export const Toast = async (
  title,
  html,
  type = 'success',
  toast = true,
  timer = 3000,
  showConfirmButton = false,
  allowOutsideClick = true,
) => {
  Swal.fire({
    title,
    html,
    type,
    toast,
    timer,
    showConfirmButton,
    allowOutsideClick,
  });
};

export const InviteFriendsModal = async (
  title,
  subTitle,
  text,
  footer,
  callback,
) => {
  Swal.fire({
    title: `<h4 style='font-weight:400;'>${title}</h4>`,
    type: 'success',
    html: `<h6 style='font-weight:400' data-testid='Friends'>${subTitle}</h6><br/> <span>${text}</span>`,
    confirmButtonText: footer,
    showConfirmButton: true,
    preConfirm: () => {
      copy(text);
      if (callback) {
        callback();
      }
    },
  });
};

export const getName = async (title, confirmButtonText) => {
  const { value: result } = await Modal.fire({
    title,
    input: 'text',
    inputAttributes: {
      autocapitalize: 'off',
    },
    showCancelButton: true,
    confirmButtonText,
    showLoaderOnConfirm: true,
    preConfirm: (login) => login,
    allowOutsideClick: () => !Swal.isLoading(),
  });
  return result;
};

export const CreateFolderModal = async (data) => {
  const result = await getName('Create Folder', 'Create');
  if (result) {
    return {
      originalName: result.trim(),
      name: result.trim().toLocaleLowerCase(),
      parentId: data.parentId,
    };
  }
  return {};
};

export const CreateFileModal = async (DocumentTypes, parentId) => {
  const result = await Modal.fire({
    title: 'Create File',
    html: `<input type="text" id="swal-input" class="swal2-input" autocomplete="off">
      <select id="swal-select" class="swal2-select"> ${DocumentTypes.map(
        (data) => `<option value="${data.templateId}">${data.name}</option>`,
      )} </select>`,
    focusConfirm: false,
    preConfirm: () => [
      document.getElementById('swal-input').value.trim(),
      document.getElementById('swal-select').value,
    ],
    showCancelButton: true,
    confirmButtonText: 'Create',
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
  });

  if (!Object.keys(result).includes('dismiss')) {
    if (result.value[0])
      return {
        originalName: result.value[0],
        name: result.value[0].toLocaleLowerCase(),
        parentId,
        templateType: result.value[1],
      };
    return {};
  }
  return {};
};

const getOptions = (data) => {
  const option =
    data !== AccessTypes.REMOVE && data !== AccessTypes.OWNER
      ? `<option value="${data}">${data}</option>`
      : '';
  return option;
};

export const ShareModal = async () => {
  const AccessKeys = Object.keys(AccessTypes);
  const result = await Modal.fire({
    title: 'Share Items',
    html: `<input type="email" placeholder="Enter Email" id="swal-input" class="swal2-input" autocomplete="off" required>
      <select id="swal-select" class="swal2-select"> ${AccessKeys.map((data) =>
        getOptions(AccessTypes[data]),
      )} </select>`,
    focusConfirm: false,
    preConfirm: () => [
      document.getElementById('swal-input').value.trim(),
      document.getElementById('swal-select').value,
    ],
    showCancelButton: true,
    confirmButtonText: 'Share',
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
  });

  if (!Object.keys(result).includes('dismiss')) {
    if (result.value[0])
      return {
        name: result.value[0].toLocaleLowerCase(),
        accessType: result.value[1],
      };
    return {};
  }
  return {};
};

export const RenameModal = async () => {
  const result = await getName('Rename', 'Save');
  if (result) {
    return {
      originalName: result.trim(),
      name: result.trim().toLocaleLowerCase(),
    };
  }
  return {};
};
